// ============================================================
// PartyContactSection.tsx
// Stage 3 (approved mockup casevo-case-sections-batch2.html §1-2): ONE
// "party contact" registry rendered by three case sections — Medical agency,
// Medical expert and Third-party solicitor (TPS). They share the same 9 flat
// Case columns (<prefix>Name / AddressLine1-2 / AddressTown / AddressCounty /
// AddressPostcode / Phone / Email / Notes), so one component drives all three
// on the shared SectionCards engine (live-view notes, card presence,
// instant save, Edit all, section footer).
//
// DATA LOGIC IS UNCHANGED from the old long forms (MedicalAgencySection /
// MedicalExpertSection / TPSSection @ b9a554e):
//   • every input writes ONE column via updateCase({ <column>: value }) →
//     the debounced case autosave; same fieldKeys (presence + live sync)
//   • AddressLookup writes all 5 address columns in ONE updateCase +
//     broadcastFieldEdits(<prefix>.address-line-1 … postcode)
//   • postcode uppercased; no required fields (Mark complete always allowed)
//   • Notes = the same Yjs doc `case:<id>:<prefix>Notes`, snapshot →
//     updateCase({ <prefix>Notes }) via forOpenCase
//   • markComplete(<sectionKey>, next) + advanceCaseSection (SectionFooter)
// ============================================================
import React from 'react'
import PhoneLink, { PHONE_GLANCE_LINK } from '../../shared/PhoneLink'
import { useCasevoStore } from '../../../store/useCasevoStore'
import { forOpenCase } from '../../../utils/caseSnapshotGuard'
import type { Case } from '../../../types/case.types'
import type { CompletableSection } from '../../../store/useSectionCompletion'
import FormField from '../../shared/FormField'
import AddressLookup from '../../shared/AddressLookup'
import RichTextEditor from '../../shared/RichTextEditor'
import { useCollabUser } from '../../../hooks/useCollabUser'
import { broadcastFieldEdits } from '../../../hooks/usePresence'
import { flushAutoSave } from '../../../hooks/useAutoSave'
import DOMPurify from 'dompurify'
import { inpCls, FGRID } from '../../clientInfo/clientInfoRegistry'
import SectionCards, { type CardDef } from './SectionCards'
import {
  KIcon, Glance, GlanceDash, SectionFooter, Kv, Empty, AddressBlock, LiveDocEdit, LiveDocView, Clamp,
  DL, BIG, MONO, CHIP_N, LINK, htmlHasText, type KitIconName,
} from './sectionKit'

export type PartyPrefix = 'medicalAgency' | 'medicalExpert' | 'tps'
export interface PartyConfig {
  prefix: PartyPrefix
  sectionKey: CompletableSection
  title: string          // section h2
  sub: string            // section sub
  cardTitle: string      // contact card title
  icon: KitIconName
  nameLabel: string      // "Agency name"
  nameKey: string        // presence/live-sync key of the name field (legacy slug)
  who: string            // "the agency"
  notesPlaceholder: string
  readySub: string       // footer sub when ready
}

// The 9 columns for a prefix (all `string` on Case).
type Col = keyof Case
const cols = (p: PartyPrefix) => ({
  name: `${p}Name` as Col,
  l1: `${p}AddressLine1` as Col,
  l2: `${p}AddressLine2` as Col,
  town: `${p}AddressTown` as Col,
  county: `${p}AddressCounty` as Col,
  pc: `${p}AddressPostcode` as Col,
  phone: `${p}Phone` as Col,
  email: `${p}Email` as Col,
  notes: `${p}Notes` as Col,
})

const PartyContactSection: React.FC<{ config: PartyConfig }> = ({ config }) => {
  const { currentCase, updateCase } = useCasevoStore()
  // Collab identity for the Yjs carets on the notes doc. Hook before the return.
  const collabUser = useCollabUser()
  if (!currentCase) return null
  const { prefix } = config
  const C = cols(prefix)
  const v = (c: Col): string => (currentCase[c] as unknown as string) ?? ''
  // Field → existing fieldKey (presence, live sync, Cancel broadcast).
  const FIELD_KEY: Partial<Record<Col, string>> = {
    [C.name]: config.nameKey,
    [C.l1]: `${prefix}.address-line-1`,
    [C.l2]: `${prefix}.address-line-2`,
    [C.town]: `${prefix}.town`,
    [C.county]: `${prefix}.county`,
    [C.pc]: `${prefix}.postcode`,
    [C.phone]: `${prefix}.phone`,
    [C.email]: `${prefix}.email`,
  }
  const ADDRESS_FIELD_KEYS = [`${prefix}.address-line-1`, `${prefix}.address-line-2`, `${prefix}.town`, `${prefix}.county`, `${prefix}.postcode`]
  const set = (c: Col, value: string): void => updateCase({ [c]: value } as Partial<Case>)
  const notesDoc = `case:${currentCase.id}:${prefix}Notes`
  const notesHtml = v(C.notes)
  const notesSnapshot = forOpenCase(currentCase.id, (html: string) => updateCase({ [C.notes]: html } as Partial<Case>))

  const text = (ctx: { idPrefix: string }, c: Col, label: string, opts: { type?: string; placeholder?: string; full?: boolean; upper?: boolean; mono?: boolean } = {}): React.ReactNode => (
    <div className={opts.full ? 'sm:col-span-2' : ''}>
      <FormField variant="info" label={label} fieldKey={FIELD_KEY[c]} htmlFor={`${ctx.idPrefix}-${String(c)}`}>
        <input
          id={`${ctx.idPrefix}-${String(c)}`}
          type={opts.type ?? 'text'}
          value={v(c)}
          onChange={(e) => set(c, opts.upper ? e.target.value.toUpperCase() : e.target.value)}
          placeholder={opts.placeholder}
          className={`${inpCls(false)} ${opts.mono ? 'font-mono tracking-[0.04em]' : ''}`}
        />
      </FormField>
    </div>
  )
  const addrLines = [v(C.l1), v(C.l2), [v(C.town), v(C.county)].filter((s) => s.trim()).join(', ')].map((s) => s.trim()).filter(Boolean)
  const pc = v(C.pc).trim()

  const defs: CardDef<Case>[] = [
    {
      key: 'contact', title: config.cardTitle, sub: 'Who and how to reach them', icon: <KIcon name={config.icon} size={17} />,
      fields: [C.name, C.phone, C.email],
      view: (ctx) => (
        <dl className={DL}>
          <Kv label={config.nameLabel} full>{v(C.name).trim() ? <span className={BIG}>{v(C.name)}</span> : <Empty onAdd={ctx.onAdd} />}</Kv>
          <Kv label="Phone">{v(C.phone).trim() ? <PhoneLink value={v(C.phone)} className={LINK} /> : <Empty onAdd={ctx.onAdd} />}</Kv>
          <Kv label="Email">{v(C.email).trim() ? <a className={`${LINK} [overflow-wrap:anywhere]`} href={`mailto:${v(C.email).trim()}`}>{v(C.email)}</a> : <Empty onAdd={ctx.onAdd} />}</Kv>
        </dl>
      ),
      edit: (ctx) => (
        <div className={FGRID}>
          {text(ctx, C.name, config.nameLabel, { full: true })}
          {text(ctx, C.phone, 'Phone', { type: 'tel', placeholder: '07700 900000' })}
          {text(ctx, C.email, 'Email', { type: 'email', placeholder: 'name@example.com' })}
        </div>
      ),
    },
    {
      key: 'address', title: 'Address', sub: 'Postal address for correspondence', icon: <KIcon name="map" size={17} />,
      fields: [C.l1, C.l2, C.town, C.county, C.pc],
      presenceKeys: [...ADDRESS_FIELD_KEYS, `${prefix}.address-search`],
      view: (ctx) => (
        <dl className={DL}>
          <Kv label="Address" full>{addrLines.length || pc ? <AddressBlock lines={addrLines} postcode={pc || undefined} /> : <Empty onAdd={ctx.onAdd} />}</Kv>
        </dl>
      ),
      edit: (ctx) => (
        <div className={FGRID}>
          <div className="sm:col-span-2">
            <FormField variant="info" label="Search address" fieldKey={`${prefix}.address-search`} htmlFor={`${ctx.idPrefix}-search`}>
              {/* Picks a suggestion and writes all 5 columns in ONE updateCase. */}
              <AddressLookup
                variant="info"
                inputId={`${ctx.idPrefix}-search`}
                value={
                  v(C.l1) || v(C.l2) || v(C.town) || v(C.county) || v(C.pc)
                    ? { line1: v(C.l1), line2: v(C.l2), town: v(C.town), county: v(C.county), postcode: v(C.pc) }
                    : null
                }
                onSelect={(addr) => {
                  updateCase({ [C.l1]: addr.line1, [C.l2]: addr.line2, [C.town]: addr.town, [C.county]: addr.county, [C.pc]: addr.postcode } as Partial<Case>)
                  broadcastFieldEdits(ADDRESS_FIELD_KEYS)
                }}
                onClear={() => {
                  updateCase({ [C.l1]: '', [C.l2]: '', [C.town]: '', [C.county]: '', [C.pc]: '' } as Partial<Case>)
                  broadcastFieldEdits(ADDRESS_FIELD_KEYS)
                }}
              />
            </FormField>
          </div>
          {text(ctx, C.l1, 'Address line 1')}
          {text(ctx, C.l2, 'Address line 2')}
          {text(ctx, C.town, 'Town')}
          {text(ctx, C.county, 'County')}
          {text(ctx, C.pc, 'Postcode', { placeholder: 'LS6 3AB', upper: true, mono: true })}
        </div>
      ),
    },
    {
      key: 'notes', title: 'Notes', sub: `Shared notes about ${config.who}`, icon: <KIcon name="text" size={17} />, full: true,
      // Live document only — nothing for Cancel to restore.
      fields: [],
      presenceKeys: [`${prefix}.notes`],
      hint: 'A live document · saves as you type · not part of Cancel',
      headerExtra: <span className={`${CHIP_N} mr-1`}><KIcon name="sync" size={12} />Live document</span>,
      // LIVE read-only view (saved copy until synced / if collab is down).
      view: (ctx) => (
        <Clamp maxH={180} moreLabel="Read more" showMore={htmlHasText(notesHtml)}>
          <LiveDocView docName={notesDoc} html={notesHtml} onSnapshot={notesSnapshot} collabUser={collabUser} empty={<Empty onAdd={ctx.onAdd} />} />
        </Clamp>
      ),
      edit: () => (
        // Notes — the same Yjs collaborative doc as before; the HTML mirror
        // persists to <prefix>Notes via updateCase.
        <FormField variant="info" label="Notes" fieldKey={`${prefix}.notes`}>
          <LiveDocEdit>
            {(onStatus) => (
              <RichTextEditor
                collabDocName={notesDoc}
                collabToken={() => localStorage.getItem('casevo_token') ?? ''}
                initialHtml={DOMPurify.sanitize(notesHtml)}
                onSnapshot={notesSnapshot}
                placeholder={config.notesPlaceholder}
                collabUser={collabUser}
                onCollabStatus={onStatus}
              />
            )}
          </LiveDocEdit>
        </FormField>
      ),
    },
  ]

  const whoTitle = config.who.charAt(0).toUpperCase() + config.who.slice(1)
  const glance = (
    <Glance cells={[
      { icon: config.icon, label: whoTitle, value: v(C.name).trim() || <GlanceDash />, sub: config.nameLabel },
      { icon: 'phone', label: 'Phone', value: v(C.phone).trim() ? <PhoneLink value={v(C.phone)} className={PHONE_GLANCE_LINK} size="sm" /> : <GlanceDash />, sub: v(C.phone).trim() ? 'Main line' : 'Not recorded' },
      { icon: 'mail', label: 'Email', value: v(C.email).trim() || <GlanceDash />, sub: v(C.email).trim() ? 'Correspondence' : 'Not recorded' },
      {
        icon: 'map', label: 'Based in',
        value: v(C.town).trim() || pc ? <>{v(C.town)}{v(C.town).trim() && pc ? ' · ' : ''}{pc && <span className={`${MONO} !text-[13px]`}>{pc}</span>}</> : <GlanceDash />,
        sub: v(C.l1).trim() || 'Address',
      },
    ]} />
  )

  return (
    <SectionCards<Case>
      value={currentCase}
      onChange={(changes) => updateCase(changes)}
      fieldKeyOf={(f) => FIELD_KEY[f]}
      onBroadcast={broadcastFieldEdits}
      onFlush={flushAutoSave}
      errors={[]}
      defs={defs}
      title={config.title}
      sub={config.sub}
      glance={glance}
      footer={<SectionFooter sectionKey={config.sectionKey} errors={[]} readySub={config.readySub} />}
      idPrefix={`${prefix}-${currentCase.id}`}
    />
  )
}
export default PartyContactSection
